import mainApi from "./api.js";
import { BASE_URL } from "./constants.js";
import { getCepStorage, saveCepStorage } from "./cepStorage.js";

const pending = new Map();
const memory = new Map();
const validVersion = (value) => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
const validFonts = (value) => Array.isArray(value) && value.length > 0 &&
  value.every((name) => typeof name === "string" && name.trim().length > 0);

async function synchronize(type) {
  const key = `fontCatalogsV1.${type}`;
  let cached = memory.get(type);
  if (!cached) {
    try {
      const stored = getCepStorage(key);
      if (stored && stored.baseUrl === BASE_URL &&
          validVersion(stored.version) && validFonts(stored.fonts)) cached = stored;
    } catch (error) {
      // A damaged or unavailable settings file must not prevent a fresh download.
    }
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const result = await mainApi.getFontCatalog(type, cached?.version, controller.signal);
    if (!result || result.type !== type || !validVersion(result.version)) {
      throw new Error("Invalid font catalog response");
    }
    if (result.unchanged === true) {
      if (!cached || cached.version !== result.version) {
        throw new Error("Font catalog cache does not match the server");
      }
      memory.set(type, cached);
      return cached.fonts;
    }
    if (!validFonts(result.fonts)) throw new Error("Invalid font catalog data");
    if (cached?.version === result.version) return cached.fonts;

    const next = { baseUrl: BASE_URL, version: result.version, fonts: result.fonts };
    memory.set(type, next);
    try {
      saveCepStorage(key, next);
    } catch (error) {
      console.warn("Font catalog is available in memory; persistent cache could not be saved.");
    }
    return next.fonts;
  } catch (error) {
    if (cached) {
      memory.set(type, cached);
      console.warn(`Using the saved ${type} font catalog; synchronization failed.`);
      return cached.fonts;
    }
    throw new Error(`Cannot load the ${type} font catalog. Connect to the server and try again.`);
  } finally {
    clearTimeout(timeout);
  }
}

export function getFontCatalog(type = "google") {
  if (type !== "google" && type !== "adobe") {
    return Promise.reject(new Error("Unknown font catalog"));
  }
  if (!pending.has(type)) {
    pending.set(type, synchronize(type).finally(() => pending.delete(type)));
  }
  return pending.get(type);
}
